// The words of the email window (Dustin, 2026-09-24: one place for a job's email): what a message
// came to, why a part was left out, and the allowed-senders box as the API keeps it.

const outcomes: Record<string, string> = {
  accepted: 'became documents',
  no_pages: 'had nothing Paige reads',
  sender_refused: 'was refused: sender not allowed',
  held: 'is waiting: email is on hold',
  disabled: 'was refused: email was off',
  fetching: 'was received and is being fetched',
  queued: 'is being read',
  waiting_for_credits: 'is waiting: the company has no pages left',
  job_off: 'is waiting: the job is switched off',
  deferred: 'is waiting to be taken in',
  auto_reply: 'was an automatic reply, so Paige left it',
  bounce: 'was a returned message, so Paige left it',
  loop: 'came from Paige itself, so Paige left it',
  failed: 'could not be read: ask the sender to send it again',
  lost: 'never got in, and it is no longer on its way: ask the sender to send it again',
};

/** What a message came to, in the customer's words. */
export const outcomeLine = (outcome: string): string => outcomes[outcome] ?? 'arrived';

/**
 * The provider re-posts a message Paige could not take for about 7 h 35 m (seconds, 5 min, 30 min,
 * 2 h, 5 h), and the API's signature window matches (`InboundEmailOptions.MaxAge`). A message still
 * `deferred` past eight hours is not coming on its own any more, and saying it is would be a lie.
 */
export const retryLadderMs = 8 * 60 * 60 * 1000;

/** The outcome the row shows: `deferred` past the ladder becomes `lost`. */
export function shownOutcome(outcome: string, receivedAt: string, now: Date = new Date()): string {
  const at = Date.parse(receivedAt);
  const over = Number.isFinite(at) && now.getTime() - at > retryLadderMs;
  return outcome === 'deferred' && over ? 'lost' : outcome;
}

/** What a message came to, as the one or two words on its row's tag. */
const tags: Record<string, string> = {
  accepted: 'Documents',
  no_pages: 'Nothing to read',
  sender_refused: 'Refused',
  disabled: 'Refused',
  fetching: 'Received — fetching',
  queued: 'Reading',
  held: 'Waiting',
  waiting_for_credits: 'Waiting',
  job_off: 'Waiting',
  deferred: 'Waiting',
  auto_reply: 'Left alone',
  bounce: 'Left alone',
  loop: 'Left alone',
  failed: 'Unreadable',
  lost: 'Not coming',
};

export const outcomeTag = (outcome: string): string => tags[outcome] ?? 'Arrived';

/** The intake parser's reason codes (MailSkipReason), in the words the sender's note uses too. */
const reasons: Record<string, string> = {
  inline_image: 'a picture in the message itself (a logo or signature)',
  small_image: 'an image too small to be a page',
  signature: "the message's digital signature",
  contact_card: 'a contact card',
  calendar_invite: 'a calendar invitation',
  unsupported_type: 'a kind of file Paige does not read (send PDF, TIFF, PNG or JPEG)',
  encrypted_pdf: 'a password-protected PDF (send it without the password)',
  damaged_file: 'a file that could not be opened',
  duplicate: 'the same file again',
  empty: 'an empty file',
  too_large: 'a file larger than Paige takes by email',
  too_many_pages: 'a file of more pages than Paige reads in one file (split it and send it again)',
  archive_limit: 'a ZIP too large or too deep to open',
  html_not_read: 'a web page attachment (this job does not read those)',
  too_many_documents: 'more documents than one email may carry',
  sender_refused: 'a message from a sender this job does not take mail from',
  not_fetched: 'a message Paige could not fetch',
};

export const skipSentence = (reason: string | null | undefined): string =>
  (reason && reasons[reason]) || 'not read';

const domainPattern = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;
const localPattern = /^[^@\s]+$/;

const senderLines = (text: string): string[] =>
  text
    .split(/[\n,;]+/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

/**
 * One line of the allowed-senders box as the API keeps it: an address, or `@vendor.example` for a
 * whole domain (a bare `vendor.example` reads the same). Null when it is neither, so the window
 * names it instead of dropping it in silence.
 */
export function senderRule(line: string): string | null {
  const text = line.trim().toLowerCase();
  if (text.length === 0) return null;
  const at = text.lastIndexOf('@');
  const domain = at < 0 ? text : text.slice(at + 1);
  if (!domainPattern.test(domain)) return null;
  if (at <= 0) return `@${domain}`;
  return localPattern.test(text.slice(0, at)) ? text : null;
}

/** The box, one entry per line or comma-separated, to the list the API keeps. */
export function parseSenders(text: string): string[] {
  return [
    ...new Set(
      senderLines(text)
        .map(senderRule)
        .filter((r): r is string => r !== null),
    ),
  ];
}

/** The lines Paige cannot keep: neither an address nor a domain. */
export const unusableSenders = (text: string): string[] =>
  senderLines(text).filter((line) => senderRule(line) === null);
